import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUploadManager, type UploadJob } from '../services/uploadManager';
import { useAuthContext } from './AuthContext';

/**
 * UploadManagerContext
 *
 * React context wrapping the singleton UploadManager. Subscribes to job state
 * changes and exposes them reactively so any component can render upload
 * progress, retry failed jobs, etc.
 *
 * Uses a window-level singleton to guarantee a single context object across
 * all module instances (prevents Vite HMR module duplication).
 */

interface UploadManagerContextValue {
  jobs: UploadJob[];
  isOnline: boolean;
  retry: (jobId: number) => void;
  cancel: (jobId: number) => void;
  dismiss: (jobId: number) => void;
}

const UPLOAD_CTX_DEFAULT: UploadManagerContextValue = {
  jobs: [],
  isOnline: true,
  retry: () => {},
  cancel: () => {},
  dismiss: () => {},
};

const CTX_KEY = '__DEKKO_UPLOAD_CTX__';
const UploadManagerContext: React.Context<UploadManagerContextValue> =
  (window as any)[CTX_KEY] ||
  ((window as any)[CTX_KEY] = createContext<UploadManagerContextValue>(UPLOAD_CTX_DEFAULT));

export const UploadManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mgr = getUploadManager();
  const { user } = useAuthContext();
  const [jobs, setJobs] = useState<UploadJob[]>(mgr.getJobs());
  const [isOnline, setIsOnline] = useState(mgr.isOnline);

  useEffect(() => {
    const unsub = mgr.subscribe(setJobs);

    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Recover orphaned uploads from IndexedDB on mount
    mgr.recover();

    return () => {
      unsub();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [mgr]);

  // recover() above runs before Firebase has restored the session, and the
  // manager refuses to upload without a user — so nothing it re-queued would
  // ever start. Kick the queue the moment a user appears (boot restore, or a
  // fresh sign-in after being logged out mid-upload).
  useEffect(() => {
    if (user) mgr.resume();
  }, [user, mgr]);

  // Closing the tab mid-upload is safe for IndexedDB-backed jobs (recovered
  // next open) and fatal for in-memory ones. Either way, ask first. Mobile
  // browsers mostly ignore this; desktop honours it.
  useEffect(() => {
    const active = jobs.some(j => j.status !== 'done' && j.status !== 'failed');
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [jobs]);

  const retry = useCallback((id: number) => mgr.retry(id), [mgr]);
  const cancel = useCallback((id: number) => mgr.cancel(id), [mgr]);
  const dismiss = useCallback((id: number) => mgr.dismiss(id), [mgr]);

  return (
    <UploadManagerContext.Provider value={{ jobs, isOnline, retry, cancel, dismiss }}>
      {children}
    </UploadManagerContext.Provider>
  );
};

export const useUploadManager = (): UploadManagerContextValue => {
  return useContext(UploadManagerContext);
};

export default UploadManagerContext;
